import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";

const styles = `
.ccl-wrap{display:flex;flex-direction:column;gap:16px;color:#0f172a}
.ccl-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap}
.ccl-title{margin:0;font-size:24px;font-weight:800;color:#0f172a}
.ccl-sub{margin:6px 0 0;color:#64748b;font-size:14px;max-width:760px}
.ccl-toolbar{display:flex;gap:10px;flex-wrap:wrap;align-items:center}
.ccl-input,.ccl-btn{border-radius:12px;font-size:13px}
.ccl-input{border:1px solid #cbd5e1;padding:11px 12px;background:#fff;min-width:260px}
.ccl-btn{border:1px solid #4338ca;background:#4338ca;color:#fff;padding:11px 16px;font-weight:700;cursor:pointer}
.ccl-btn:disabled{opacity:.6;cursor:not-allowed}
.ccl-card{background:#fff;border:1px solid #e2e8f0;border-radius:18px;box-shadow:0 12px 32px rgba(15,23,42,.06);overflow:hidden}
.ccl-table-wrap{overflow:auto}
.ccl-table{width:100%;border-collapse:collapse;min-width:880px}
.ccl-table th{background:#f8fafc;padding:13px 16px;text-align:left;font-size:12px;font-weight:800;color:#334155;text-transform:uppercase}
.ccl-table td{padding:14px 16px;border-top:1px solid #f1f5f9;font-size:13px;color:#0f172a;vertical-align:middle}
.ccl-name{display:flex;flex-direction:column;gap:2px}
.ccl-name strong{font-size:13px}
.ccl-name span{font-size:12px;color:#64748b}
.ccl-limit{display:flex;gap:8px;align-items:center}
.ccl-limit input{width:150px;border:1px solid #cbd5e1;border-radius:10px;padding:9px 10px}
.ccl-save{border:1px solid #0f766e;background:#0f766e;color:#fff;border-radius:10px;padding:9px 12px;font-weight:700;cursor:pointer}
.ccl-save:disabled{opacity:.6;cursor:not-allowed}
.ccl-empty{padding:32px 20px;text-align:center;color:#64748b}
.ccl-msg{padding:12px 14px;border-radius:12px;font-size:13px;font-weight:600}
.ccl-msg.ok{background:#ecfdf5;color:#047857;border:1px solid #a7f3d0}
.ccl-msg.err{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca}
`;

const fmtCurrency = (value) => `Rs ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CustomerCreditLimits() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await api.get("/customers", { params: { per_page: 200 } });
      const data = res?.data?.data || [];
      setRows(data);
      setDrafts(
        data.reduce((acc, row) => {
          acc[row.id] = row.credit_limit ?? 0;
          return acc;
        }, {})
      );
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.message || "Failed to load customers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [row.name, row.phone, row.email, row.code]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [rows, query]);

  const updateDraft = (id, value) => {
    setDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const saveCreditLimit = async (customerId) => {
    setSavingId(customerId);
    setError("");
    setMessage("");
    try {
      const creditLimit = drafts[customerId] === "" ? 0 : Number(drafts[customerId]);
      await api.put(`/customers/${customerId}`, { credit_limit: creditLimit });
      setRows((prev) => prev.map((row) => (row.id === customerId ? { ...row, credit_limit: creditLimit } : row)));
      setMessage("Customer credit limit updated successfully.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update customer credit limit.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="ccl-wrap">
        <div className="ccl-head">
          <div>
            <h1 className="ccl-title">Customer Credit Limits</h1>
            <p className="ccl-sub">Admins can review and update customer credit limits from one place.</p>
          </div>
          <div className="ccl-toolbar">
            <input
              className="ccl-input"
              placeholder="Search customer by name, phone, code"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="ccl-btn" onClick={load} disabled={loading}>Refresh</button>
          </div>
        </div>

        {message ? <div className="ccl-msg ok">{message}</div> : null}
        {error ? <div className="ccl-msg err">{error}</div> : null}

        <div className="ccl-card">
          <div className="ccl-table-wrap">
            <table className="ccl-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Current Credit Limit</th>
                  <th>Update Credit Limit</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="ccl-empty">Loading customers...</td></tr>
                ) : visibleRows.length === 0 ? (
                  <tr><td colSpan={4} className="ccl-empty">No customers found.</td></tr>
                ) : visibleRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="ccl-name">
                        <strong>{row.name}</strong>
                        <span>{row.code || "-"} {row.email ? `| ${row.email}` : ""}</span>
                      </div>
                    </td>
                    <td>{row.phone || "-"}</td>
                    <td>{fmtCurrency(row.credit_limit)}</td>
                    <td>
                      <div className="ccl-limit">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={drafts[row.id] ?? ""}
                          onChange={(e) => updateDraft(row.id, e.target.value)}
                        />
                        <button
                          className="ccl-save"
                          onClick={() => saveCreditLimit(row.id)}
                          disabled={savingId === row.id}
                        >
                          {savingId === row.id ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
